import hyperdom from 'hyperdom'
import pluralize from 'pluralize'
import {get} from 'lodash-es'
import format from 'root-core/utils/text/stringFormatFilter.js'
import formats from '../../../../../../packages/dates/formats.js'
import formatDate from '../../../../../../packages/dates/formatDate.js'
import LocalDate from '@booking-services/localDate'
import baggageNotes from '../../../flights/baggageNotes.jsx'
import assetPath from '../../../assetPath.js'
import placeholderPolyfill from '../../../placeholderPolyfill.jsx'
import ConfirmationTabSchema from './confirmationTabSchema.js'
import * as validation from '../../../validation.js'
import scroller from '../../../scroller.js'
import TripSummary from './tripSummary.jsx'
import customSelect from '../../../customSelect.jsx'
import repositionAbsoluteElement from '../../../repositionAbsoluteElement.js'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import {Message, MessageTypes} from '../../../message.jsx'

export default class ConfirmationTab {
  constructor (options) {
    this.config = options.config
    this.confirmedPayments = []
    this.countries = options.countries
    this.shareConfirmationEmail = {
      emailAddresses: [{
        firstName: '',
        emailAddress: '',
      }],
    }
    this.feedback = {}
    this.feedbackSent = false
    this.showLoading = options.showLoading
    this.hideLoading = options.hideLoading
    this.api = options.api
    this.departure = options.departure
    this.booking = options.booking
    this.user = options.user
    this.serverErrorsPanel = options.serverErrorsPanel
    this.browserCapabilities = options.browserCapabilities
    this.schema = new ConfirmationTabSchema({
      config: this.config,
    }).buildSchema()
    this.paperDocsUpdateTriggered = false
    this.paperDocsSelected = false
    this.paperDocsUpdateError = false
    this.paymentCurrencyCode = options.departure.prices.currency
    this.tourHeader = options.tourHeader
    this.modals = options.modals
    this.getTripDetails = options.getTripDetails
    this.tripSummary = new TripSummary({
      departure: options.departure,
      getTripDetails: this.getTripDetails,
      config: this.config,
    })
    this.isExtoleEnabled = false
    this.extoleZoneCreated = false
    this.knoCommerce = options.knoCommerce
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  navigable () {
    return !!this.booking.confirmation
  }

  shouldCreateExtole () {
    const {payment: {amount, outstanding}} = this.getTripDetails()
    return amount > 0 && outstanding <= 0 && get(this.config.features, 'extole.enabled')
  }

  createExtoleZone () {
    const extoleElement = document.getElementById('extole_zone_confirmation')
    if (window.extole && extoleElement && !this.extoleZoneCreated) {
      const confirmationTag = document.createElement('script')
      confirmationTag.type = 'text/javascript'
      confirmationTag.text = `(function(c,b,f,k,a){c[b]=c[b]||{};for(c[b].q=c[b].q||[];a<k.length;)f(k[a++],c[b])})(window,"extole",function (c,b){b[c]=b[c]||function (){b.q.push([c,arguments])}},["createZone"],0);
      extole.createZone({
          name: "confirmation",
          element_id: 'extole_zone_confirmation',
          data: {
            locale: '${get(this.config.features, 'extole.locale', 'en_US')}',
          }
      });`
      document.head.appendChild(confirmationTag)
      this.extoleZoneCreated = true
    }
  }

  initKnoCommerce () {
    if (!this.knoCommerce) {
      return
    }

    this.knoCommerce.init({
      booking: this.booking.booking,
      tripDetails: this.getTripDetails(),
    })
  }

  loadTab () {
    this.initKnoCommerce()
    this.isExtoleEnabled = this.shouldCreateExtole()
    this.serverErrorsPanel.clear('payment')
    this.confirmation = this.loadConfirmation()
    this.confirmation.paymentHeader = (headerText, additionalText, imageName) => {
      return [<div class='u-clearfix green-box'>
        <div className='confirmation__payment-status'>
          <div className='confirmation__payment-status-row'>
            {imageName !== 'paid'
              ? <img className='confirmation__payment-status-image' src={assetPath(`/public/img/${imageName}.svg`)} alt='success'/>
              : <div class='confirmation__payment-status-icon -new-ui_solid_check_circle'></div>
            }
            <div className='confirmation__payment-status-header'>{headerText}</div>
          </div>
          <div className='confirmation__reservation-code'>Your reservation code: <strong class="confirmation__booking-reference__code">{this.booking.booking.bookingReference}</strong></div>
          {this.booking.confirmation.onHold
            ? ''
            : <a class='btn-secondary confirmation__mmb-button' href={this.config.confirmation.MTP_callToAction}>MANAGE MY BOOKING</a>
          }
        </div>
      </div>,
      <div>
        {this.config.features.showPayNowButton
          ? <div className='u-clearfix blue-box'>
            <div className='confirmation__payment-status'>
              <div class='confirmation__payment-status-subheader'>{additionalText}</div>
              {this.booking.confirmation.onHold && !this.user.isTravelAgent
                ? <a class='btn-primary confirmation__mmb-button' href={this.config.confirmation.MTP_callToAction}>PAY NOW</a>
                : ''
              }
            </div>
          </div>
          : ''
        }
      </div>]
    }
    this.loaded = true
  }

  confirmBooking () {
    const booking = get(this, 'booking.booking')

    if (!booking) return

    const {bookingReference, totalAmount} = booking
    const alreadyConfirmed = this.confirmedPayments.some(confirmedPayment => confirmedPayment === bookingReference)

    if (alreadyConfirmed) return

    this.confirmedPayments.push(bookingReference)
    this.api.confirmSale({bookingReference, totalAmount, paymentCurrencyCode: this.paymentCurrencyCode})
  }

  loadConfirmation () {
    if (this.booking.confirmation.onHold) {
      return new OnHoldConfirmation(this.booking.confirmation, this.booking.booking)
    } else if (this.booking.confirmation.creditAgreementPayment) {
      this.confirmBooking()
      return new CreditAgreementConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.creditCardPayment) {
      this.confirmBooking()
      return new CreditCardConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.directDeposit) {
      this.confirmBooking()
      return new DirectDepositConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.tourOnRequest) {
      return new TourOnRequestConfirmation(this.booking.confirmation, this.config)
    } else if (this.booking.confirmation.cheque) {
      this.confirmBooking()
      return new ChequeConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.mcowra) {
      this.confirmBooking()
      return new MCOWRAConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.paymentGate) {
      return new PaymentGateConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.electronicFundsTransfer) {
      return new ElectronicFundsTransferConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.eNett) {
      return new EnettConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.smartMoney) {
      this.confirmBooking()
      return new SmartMoneyConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.paypalPayment) {
      this.confirmBooking()
      return new PayPalConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.fake) {
      this.confirmBooking()
      return new FakeConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.zipMoney) {
      this.confirmBooking()
      return new ZipMoneyConfirmation(this.booking.confirmation)
    } else if (this.booking.confirmation.paymentGateway) {
      this.confirmBooking()
      switch (this.booking.confirmation.paymentGateway.paymentMethod) {
      case 'creditcard':
        return new CreditCardConfirmation(this.booking.confirmation)
      case 'paypal':
        return new PayPalConfirmation(this.booking.confirmation)
      case 'banktransfer':
        return new DirectDepositConfirmation(this.booking.confirmation)
      case 'credit_agreement':
        return new CreditAgreementConfirmation(this.booking.confirmation)
      default:
        return new PaymentGatewayConfirmation(this.booking.confirmation)
      }
    } else if (this.booking.confirmation.unknown) {
      this.confirmBooking()
      return new UnknownConfirmation(this.booking.confirmation)
    }
  }

  pageTitle () {
    return this.confirmation.pageTitle() + ` Confirmation - ${this.booking.booking.bookingReference}`
  }

  sendShareConfirmationEmail () {
    this.showLoading()
    return this.api.sendShareConfirmationEmail(this.booking.booking.bookingReference, this.shareConfirmationEmail.emailAddresses).then(() => {
      this.api.onSuccess()
      this.hideLoading()
      this.shareConfirmationEmail = {emailAddresses: [{firstName: '', emailAddress: ''}], sent: true}
    }).catch((error) => this.api.onError(error))
  }

  agentTAPLoginLink () {
    const {sellingCompanyCode, bookingId} = this.booking.booking
    const TapURL = this.config.confirmation.TAP_callToAction_url
    return `${TapURL}/en-us/booking/${bookingId}/${sellingCompanyCode}`
  }

  showRegistrationDetailsConfirmation () {
    this.registrationDetailsConfirmationDone = true
  }

  showRegistrationDetailsConfirmationWarning () {
    this.registrationDetailsConfirmationWarning = true
  }

  choosePaperDocuments () {
    if (!this.paperDocsUpdateTriggered) {
      this.paperDocsUpdateTriggered = true
      return this.booking.choosePaperDocuments()
        .then(response => {
          if (response && response.body.status === 'failed') {
            return Promise.reject(response.body.errorMessage)
          }
          this.paperDocsSelected = true
        }).catch(() => {
          this.paperDocsUpdateError = true
        })
    }
  }

  submitRegistrationDetails () {
    const {leadPassenger} = this.booking.booking

    const countryCode = leadPassenger.mobileNumber.countryCode
    if (countryCode) {
      const country = this.countries()[countryCode.toLowerCase()] || {}
      leadPassenger.mobileNumber.internationalCode = country.internationalCallingCode || this.config.internationalCode
    }

    if (!this.validate().valid) {
      this.hasScrolledToError = false
      return
    }

    return this.booking.changeRegistrationDetails()
      .then(response => {
        if (response && response.body.status === 'failed') {
          return Promise.reject(response.body.errorMessage)
        }
        this.showRegistrationDetailsConfirmation()
      })
      .catch(error => {
        this.showRegistrationDetailsConfirmationWarning(error)
      })
  }

  renderRegistrationDetailsConfirmationWarning () {
    const message = `<span>Oh Dear!</span> Looks like we can't submit your details at this time, not to worry you can add them at a later date using <a class='mmb' data-id='mmb-link' href='{0}'>My Travel Portal</a>`
    return hyperdom.rawHtml('div', format(message, this.config.confirmation.MTP_callToAction))
  }

  renderRegistrationDetailsConfirmationDone () {
    const message = `<span>All Done!</span> Thanks for submitting your details, if you need to make any other changes please go the <a class='mmb' data-id='mmb-link' href='{0}'>My Travel Portal</a>`
    return hyperdom.rawHtml('div', format(message, this.config.confirmation.MTP_callToAction))
  }

  renderValidationError (model, property, opts = {}) {
    if (this.showValidationErrors || (model._visited && model._visited.indexOf(property) != -1)) {
      let error
      if (this.validation) {
        error = this.validation.error(model, property)
      }
      if (error) {
        const s = this.hasScrolledToError ? undefined : scroller.renderScroller()
        this.hasScrolledToError = true
        return <div class={`o-form-item__warning ${opts.cssClass}`}>
          { s }
          <span>{error}</span>
        </div>
      }
    }
  }
  isFieldInvalid (model, property) {
    if(this.showValidationErrors && this.validation) {
      return !!this.validation.error(model, property)
    }
    return false
  }

  fieldVisited (model, property) {
    this.validation = validation.fieldVisited(this.booking.booking, this.schema, model, property)
  }

  validate () {
    this.validation = validation.validate(this.booking.booking, this.schema)
    this.showValidationErrors = !this.validation.valid
    return this.validation
  }

  renderEdocsUpdate () {
    if (!this.paperDocsUpdateError) {
      return this.paperDocsSelected
        ? <span class="confirmation__edocs__message edocs__message-success icon-tick">You've chosen to receive paper documents.</span>
        : <button class="confirmation__edocs__paper" onclick={() => this.choosePaperDocuments()}>Use paper documents instead</button>
    } else {
      return <div class='confirmation__edocs__message edocs__message-error'>
        {this.renderRegistrationDetailsConfirmationWarning()}
      </div>
    }
  }

  renderEdocs () {
    if (this.config.features.showEdocs) {
      const forceEdocs = this.config.features.forceEdocs
      return <div class={['o-accent-box-adjacent', 'confirmation__edocs']}>
        <div class='confirmation__edocs__inner'>
          <div class='confirmation__edocs__header'>
            We use e-docs for a greener future.
          </div>
          <div class='confirmation__edocs__text'>{forceEdocs ? this.config.confirmation.eDocsConfirmation : this.config.confirmation.paperDocsConfirmation}</div>
          {!forceEdocs ? this.renderEdocsUpdate () : undefined}
        </div>
      </div>
    }
  }

  renderLeadPassengerConfirmation () {
    const leadPassenger = this.booking.booking.leadPassenger
    const leadPassengerMessageClasses = []
    const leadPassengerClasses = ['confirmation__registration__lead_passenger']
    const leadPassengerBoxClass = 'o-accent-box-white'
    leadPassengerClasses.push(leadPassengerBoxClass)
    if (!this.config.features.showEdocs) {
      leadPassengerClasses.push('confirmation__registration__legacy')
    }

    if (this.registrationDetailsConfirmationDone) {
      leadPassengerMessageClasses.push('confirmation__registration__message_success', leadPassengerBoxClass)
      return <div class={leadPassengerMessageClasses}>
        {this.renderRegistrationDetailsConfirmationDone()}
      </div>
    }

    if (this.registrationDetailsConfirmationWarning) {
      leadPassengerMessageClasses.push('confirmation__registration__message_warning', leadPassengerBoxClass)
      return <div class={leadPassengerMessageClasses}>
        {this.renderRegistrationDetailsConfirmationWarning()}
      </div>
    }

    const countries = this.countries()
    return <div class="confirmation__registration">
      <div class="confirmation__registration__header">
        <div class='confirmation__section-title confirmation__registration__header_text'>Lead Passenger Confirmation</div>
        <div class='tooltip'>
          {(() => {
            const tooltip = <div class='tooltip__content'>
              <div>
                <p>Please complete your contact details as soon as possible after booking. This helps ensure your trip runs smoothly and hassle free. Plus in the unlikely event that something happens on your trip, we’ll have all your essential details so we can get in touch with you.</p>
              </div>
            </div>
            return repositionAbsoluteElement(tooltip, 0)
          }
          )()}
          <a class='confirmation__registration__header_icon' onclick={e => {e.preventDefault()}}></a>
        </div>
      </div>
      <div class={leadPassengerClasses}>
        <form onsubmit={ev => {ev.preventDefault(); return this.submitRegistrationDetails()}}>
          <div class='confirmation__container'>
            <div class='o-form-item form-item-mobilephone'>
              <label for='mobileNumber' class={['o-form-item__label']}>
                Mobile Number <span class='field-required'>*</span>
              </label>
              <div class="confirmation__mobile__inner">
                <div class={['confirmation__mobile__item', 'form-item-country-code']}>
                  {customSelect({
                    classes: {input:  `${this.isFieldInvalid(leadPassenger.mobileNumber, 'number') ? 'visited-invalid' : ''}`},
                    inputBinding: {get: () => countries[leadPassenger.mobileNumber.countryCode.toLowerCase()].internationalCallingCode},
                    selectBinding: [leadPassenger.mobileNumber, 'countryCode'],
                    selectId: 'pax-phone-country-code',
                    selectOnBlur: () => this.fieldVisited(leadPassenger.mobileNumber, 'countryCode'),
                    options: Object.keys(countries).map(countryCode => {
                      const country = countries[countryCode]
                      return {
                        value: countryCode.toUpperCase(),
                        display: `${country.name} (${country.internationalCallingCode})`,
                      }
                    }),
                  })}
                  {this.renderValidationError(leadPassenger.mobileNumber, 'internationalCode')}
                </div>
                <div class="confirmation__mobile-number confirmation__mobile-number form-item-mobile-number">
                  {placeholderPolyfill(
                    <input
                      id='mobileNumber'
                      class={['o-form-item__txt-input', {'visited-invalid': this.isFieldInvalid(leadPassenger.mobileNumber, 'number')}]}
                      placeholder='Mobile number'
                      type='text'
                      binding={[leadPassenger.mobileNumber, 'number']}
                      onblur={() => this.fieldVisited(leadPassenger.mobileNumber, 'number')}
                      name='number' />,
                    this.browserCapabilities
                  )}
                  {this.renderValidationError(leadPassenger.mobileNumber, 'number')}
                </div>
              </div>
            </div>
            <div class='o-form-item form-item-email-address confirmation__registration__email-container'>
              <label for='emailAddress' class='o-form-item__label'>
                Email Address <span class='field-required'>*</span>
              </label>
              <div class="confirmation__registration__email">
                {placeholderPolyfill(
                  <input id='emailAddress'
                    class={['o-form-item__txt-input', {'visited-invalid': this.isFieldInvalid(leadPassenger, 'emailAddress')}]}
                    type={this.browserCapabilities.input.email}
                    placeholder='name@email.com'
                    binding={[leadPassenger, 'emailAddress']}
                    onblur={(event) => {
                      event.target.value = event.target.value.trim()
                      this.fieldVisited(leadPassenger, 'emailAddress')}}
                    name='emailAddress' />,
                  this.browserCapabilities
                )}
                {this.renderValidationError(leadPassenger, 'emailAddress')}
              </div>
            </div>
          </div>
          <div class="confirmation__container__submit-row">
            <div class='confirmation__mobile__row'>
              <label for='mobile_emergency_part' class={['form-item-number-while-away', 'o-choice-item', '-fullWidth', 'u-margin-bottom-none']}>
                <input id='mobile_emergency_part' type='checkbox'
                  class='o-choice-item__checkbox'
                  binding={[leadPassenger.mobileNumber, 'numberWhileAway']}
                />
                <div class='o-choice-item__content'>
                  <span class='mobile_emergency_part_info u-margin-right-medium'>This are the email and phone number I'd like to use on the trip</span>
                </div>
              </label>
            </div>
            <div type='submit' class='confirmation__confirm_details'>
              <button class='btn-primary'
                onclick={ev => {ev.preventDefault(); return this.submitRegistrationDetails()}}>
                Confirm Details
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  }

  render (options) {
    if (this.isExtoleEnabled) {
      this.createExtoleZone()
    }
    return <div id='overview'>
      {this.isExtoleEnabled ? <span id="extole_zone_confirmation"></span> : undefined}
      {this.knoCommerce ? this.knoCommerce.renderPopup() : undefined}
      <div data-test={'confirmation-tab'} class='confirmation u-clearfix'>
        <div class='o-accent-box-summary'>{this.confirmation.renderHeader()}</div>
        {this.serverErrorsPanel}
        {this.renderAirInfoForHiddenExtras()}
        {this.confirmation.isTourOnRequest
          ? undefined
          : <div class="o-accent-box-container">
            {this.renderLeadPassengerConfirmation()}
          </div>
        }
        {this.tourHeader}
        {this.tripSummary}
        <div class='o-accent-box-white'>
          {(() => {
            const warningText = <span>Looking for <a href={this.config.brandPages.brandWebsite}>another tour</a>?</span>
            return options.navigatedBackAfterConfirmation ?
              new Message({
                header: 'Your booking has been completed',
                message: warningText,
                type: MessageTypes.WARNING,
              }) : undefined
          })()}

          {this.confirmation.renderPaymentConfirmation()}

          <div class='confirmation__email'>
            {
              this.booking.confirmation.confirmationEmailAddress
                ? <div>
                  {this.config.confirmationEmail.enabled
                    ? <div>
                      <p>A Booking confirmation email is being sent to <strong>{this.booking.confirmation.confirmationEmailAddress}</strong>.</p>
                      <p>If you do not receive an e-mail within an hour, please contact us</p>
                    </div>
                    : undefined}
                  <p>If you have any query concerning your booking, you can contact our call center at <strong>{this.config.phone.general}</strong></p>
                </div>
                : <div class='u-margin-top-large' >
                  {new Message({
                    header: 'Warnings',
                    message: 'We\'re sorry, your confirmation email has not been sent. Please make a note of your booking reference number.',
                    type: MessageTypes.WARNING,
                  })}
                </div>

            }
          </div>
          {
            this.config.showBaggageNotes && this.booking.quoteCriteria.hasFlights()
              ? <div class='confirmation__baggage-notes'> <h2 class='u-margin-bottom-small'>baggage information</h2> {baggageNotes(this)} </div>
              : undefined
          }
        </div>
        {
          this.config.insurance && this.config.insurance.externalInsuranceProviderUrl
            ? <div class='o-accent-box-white'>
              <div class='external-insurance'>
                <h2 class='u-margin-bottom-small u-margin-top-none'>Manulife Travel Insurance for the Travel Corporation Canadian Guests</h2>
                <p>Manulife's specially designed travel insurance can help protect you from financial worries, in case of the unexpected and you can focus on what matters most, your {this.config.brandFamilyDisplayText} trip. The plan includes cover for Trip Cancellation, Interruption, Missed Connections, Baggage Loss, Damage, Delay and more. You’ll get 24/7 multilingual assistance on our helpline with the added comfort of Medical Concierge Services inclusive of physician consultations.</p>
                <p>Please click the link below to get a no obligation quote on our travel protection. In order to qualify for Manulife's pre-departure any reason cancellation benefit, the policy must be purchased within 72 hours of your tour booking.</p>
                <p><a href={this.config.insurance.externalInsuranceProviderUrl} className="external-insurance-link" target="_blank">Get your insurance quote</a></p>
                <p>For full policy coverage, please select the full duration of your trip and ensure you specify the full non-refundable value of your trip including taxes. Please have your Booking Reference number on hand.</p>
                <p>If you prefer to speak to a Manulife Licensed Advisor please call +1 844 423 3539 on Monday through Friday.</p>
              </div>
            </div>
            : undefined
        }
        {/* // TODO uncomment april 2025, according to BSP-1135 */}
        {/* {
           this.confirmation.canManageBooking    
            ? <div>
              <div class="confirmation__section-title">Manage your booking</div>
              <div class="manage-my-booking o-accent-box-white">
                { this.user.isTravelAgent
                  ?<span class='MMB-call-to-action'>{hyperdom.rawHtml('span', format(this.config.confirmation.MTP_callToAction_template, this.config.confirmation.MTP_callToAction, this.agentTAPLoginLink()))}</span>
                  :<span class='MMB-call-to-action'>{hyperdom.rawHtml('span', format(this.config.confirmation.MTP_callToAction_template, this.config.confirmation.MTP_callToAction))}</span>
                }
              </div>
            </div>
            : undefined
        } */}
        { this.config.confirmation.carbonFootprint
          ? <div class="carbon-footprint">{hyperdom.rawHtml('span', this.config.confirmation.carbonFootprint)}</div>
          : undefined
        }
        { this.renderShareConfirmationEmail() }
        {this.confirmation.isTourOnRequest
          ? undefined
          : this.renderEdocs()
        }
      </div>
    </div>
  }

  renderShareConfirmationEmail () {
    if (!this.config.confirmationEmail.enabled) {
      return
    }

    return <div className="share-confirmation-email" data-id="share-confirmation-email">
      <div className="share-confirmation-email__info-container">
        <div className="share-confirmation-email__header">Travelling with others?</div>
        <div className="share-confirmation-email__text">Share your booking with your fellow travelers.</div>
      </div>
      {
        this.shareConfirmationEmail.sent
          ? <div className="share-confirmation-email__info-message">
            <div className="share-confirmation-email__info-message-text">Your booking has been shared with your fellow
              travelers.
            </div>
            <div className="share-confirmation-email__info-message-close-button" onclick={() => {
              this.shareConfirmationEmail.sent = false
            }}/>
          </div>
          : undefined
      }
      {this.shareConfirmationEmail.emailAddresses.map((email) => {
        return <div className="share-confirmation-email__form-row">
          <div className="share-confirmation-email__form-container">
            <div className="share-confirmation-email__textbox-label">First Name *</div>
            <input
              className="share-confirmation-email__textbox-input"
              placeholder="First Name"
              binding={[email, 'firstName']}/>
          </div>
          <div className="share-confirmation-email__form-container">
            <div className="share-confirmation-email__textbox-label">Email Address *</div>
            <input
              className="share-confirmation-email__textbox-input"
              type={this.browserCapabilities.input.email}
              placeholder="Email Address"
              binding={[email, 'emailAddress']}/>
          </div>
          <button className="share-confirmation-email__form-button btn-primary" onclick={(e) => {
            e.preventDefault()
            return this.sendShareConfirmationEmail()
          }}>Send email</button>
        </div>
      })}

    </div>
  }

  renderAirInfoForHiddenExtras () {
    const contactInfo = this.config.phone.general
    return this.config.features.extrasEnabled
      ? undefined
      : <div className='confirmation__add-air-info'>
        <i className='-new-ui_plane'></i>
        <span>Want to add air to this booking? Our expert team is here to help, simply
          {contactInfo.startsWith('http')
            ? <span> contact us via <a href={contactInfo}>{contactInfo}</a></span>
            : <span> call us on <a href={'tel:' + contactInfo}>{contactInfo}</a></span>
          } after you complete your booking to find the best flights for your trip
        </span>
      </div>
  }
}

class FakeConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = true
  }

  renderHeader () {
    return this.paymentHeader('Fake payment taken successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Payment'
  }

  renderPaymentConfirmation () {
    return <div class='confirmation__payment'>
      <p>You have paid</p>
    </div>
  }
}

class UnknownConfirmation extends FakeConfirmation {
  renderHeader () {
    return this.paymentHeader('Unknown payment taken successfully!','See below for important next steps, documentation and information.','paid')
  }
}

class OnHoldConfirmation {
  constructor (confirmation, booking) {
    this.confirmation = confirmation
    this.booking = booking
    this.canManageBooking = true
  }

  renderHeader () {
    const dayDiff = new LocalDate().differenceInDays(this.confirmation.onHold.dueDate)
    const dateString = pluralize('day', dayDiff, true)
    const additionalText = dayDiff > 0 ? `Please make a payment in the next ${dateString} if you wish to proceed.` : `Please make a payment today if you wish to proceed`
    return this.paymentHeader('Your booking is being held',additionalText,'on-hold')
  }

  pageTitle () {
    return 'On Hold'
  }

  renderPaymentConfirmation () {
    return <div class='confirmation__payment'>
      <p>To confirm your booking
        {
          this.booking.paymentOptions.deposit
            ? <span> your deposit </span>
            : <span> full payment </span>
        }
       is due by {this.confirmation.onHold.dueDate ? formatDate(this.confirmation.onHold.dueDate, formats.dayMonthYear) : ''}</p>
    </div>
  }
}

class CreditAgreementConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Payment'
  }

  renderPaymentConfirmation () {
    return <div class='confirmation__payment'>
      <p>Payment by credit agreement</p>
    </div>
  }
}

class CreditCardConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = true
  }

  renderHeader () {
    return this.paymentHeader('Your payment has been processed successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Payment'
  }

  renderPaymentConfirmation () {
  }
}

class PayPalConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = true
  }

  renderHeader () {
    return this.paymentHeader('PayPal payment taken successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Payment'
  }

  renderPaymentConfirmation () {
  }
}

class PaymentGatewayConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = true
  }

  renderHeader () {
    return this.paymentHeader('Your payment has been taken successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Payment'
  }

  renderPaymentConfirmation () {
  }
}

class ZipMoneyConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = true
  }

  renderHeader () {
    if (this.confirmation.zipMoney.onHold) {
      return this.paymentHeader(
        `Your Zip Money application (reference ${this.confirmation.zipMoney.reference}) is under referral, however your spot on this trip has been secured.`,
        `When Zip Money have reached an outcome you will receive confirmation by email.`,
        'on-hold'
      )
    } else {
      return this.paymentHeader(`Zip Money payment has been processed successfully! Your Zip Money reference is ${this.confirmation.zipMoney.reference}`,'See below for important next steps, documentation and information.','paid')
    }
  }

  pageTitle () {
    return 'Payment'
  }

  renderPaymentConfirmation () {
  }
}

class DirectDepositConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = true
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Deposit Payment'
  }

  renderPaymentConfirmation () {
  }
}

class ChequeConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = false
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Cheque Payment'
  }

  renderPaymentConfirmation () {
  }
}

class PaymentGateConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = false
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Payment Gate Payment'
  }

  renderPaymentConfirmation () {
  }
}

class ElectronicFundsTransferConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = false
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Electronic Funds Transfer Payment'
  }

  renderPaymentConfirmation () {
  }
}

class EnettConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = false
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Enett Payment'
  }

  renderPaymentConfirmation () {
  }
}

class SmartMoneyConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = false
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'Smart Money Payment'
  }

  renderPaymentConfirmation () {
  }
}

class MCOWRAConfirmation {
  constructor (confirmation) {
    this.confirmation = confirmation
    this.canManageBooking = false
  }

  renderHeader () {
    return this.paymentHeader('Booking made successfully!','See below for important next steps, documentation and information.','paid')
  }

  pageTitle () {
    return 'MCOWRA Payment'
  }

  renderPaymentConfirmation () {
  }
}

class TourOnRequestConfirmation {
  constructor (confirmation, config) {
    this.confirmation = confirmation
    this.canManageBooking = false
    this.isTourOnRequest = true
    this.config = config
  }

  renderHeader () {
    const additionalText = hyperdom.rawHtml('span', format(
      this.config.confirmation.tourOnRequest,
      this.config.phone.general,
      this.config.brandPages.brandWebsite,
      this.config.brandName
    ))

    return this.paymentHeader('Your booking is on request', additionalText, 'on-request')
  }

  pageTitle () {
    return 'On Request'
  }

  renderPaymentConfirmation () {
  }
}
